import { pipe, Option, Array as A } from 'effect';

/**
 * A place in the application the user can navigate to: a tab, a section of a tab, a screen, a navigation item's
 * target. `see` — the user may know it exists; `open` — the user may enter it.
 */
export type DestinationAction = 'see' | 'open';

/**
 * The strongest action the user holds on a destination: hidden — not even `see`; locked — `see` without `open`
 * (the specifications' "shown but not selectable"); open.
 */
export type DestinationAccess = 'hidden' | 'locked' | 'open';

// Structural rather than `CaseAbility`: CASL's checks do not type over a generic subject, and a case with verbs of
// its own besides `see` and `open` must fit too.
type DestinationChecker<Destination> = {
  can(action: DestinationAction, destination: Destination): boolean;
};

export const destinationAccess = <Destination>(
  ability: DestinationChecker<Destination>,
  destination: Destination,
): DestinationAccess => {
  if (!ability.can('see', destination)) return 'hidden';
  return ability.can('open', destination) ? 'open' : 'locked';
};

/**
 * Each container's children, in landing order — the order its address looks for an open child. A key that is not a
 * container is a leaf; only leaves carry rules.
 */
export type DestinationTree<Key extends string> = Partial<Record<Key, ReadonlyArray<Key>>>;

const childrenOf = <Key extends string>(tree: DestinationTree<Key>, key: Key): ReadonlyArray<Key> => tree[key] ?? [];

/**
 * The access of any destination in a case's tree. A leaf's comes from its rules; a container has no rule of its own: open
 * when a child is open, locked when a child is seen, hidden otherwise — so a parent is never kept in step with its
 * children by hand.
 */
export const treeDestinationAccess = <Key extends string, Destination>(
  ability: DestinationChecker<Destination>,
  toDestination: (key: Key) => Destination,
  tree: DestinationTree<Key>,
) => {
  const accessOf = (key: Key): DestinationAccess => {
    const children = childrenOf(tree, key);
    if (A.isEmptyReadonlyArray(children)) return destinationAccess(ability, toDestination(key));
    const accesses = A.map(children, accessOf);
    if (A.contains(accesses, 'open')) return 'open';
    return A.contains(accesses, 'locked') ? 'locked' : 'hidden';
  };
  return accessOf;
};

/**
 * The leaf a destination leads to: itself when it is an open leaf, otherwise its first open child in landing order,
 * followed down to a leaf; none when nothing under it is open. A link to a tab goes straight to this leaf, so the
 * tab's route sagas start once.
 */
export const landingDestination = <Key extends string>(
  accessOf: (key: Key) => DestinationAccess,
  tree: DestinationTree<Key>,
) => {
  const landing = (key: Key): Option.Option<Key> => {
    const children = childrenOf(tree, key);
    if (A.isEmptyReadonlyArray(children)) return accessOf(key) === 'open' ? Option.some(key) : Option.none();
    return pipe(
      children,
      A.findFirst((child) => accessOf(child) === 'open'),
      Option.flatMap(landing),
    );
  };
  return landing;
};
